import { Avatar } from '@mui/material';
import { handleHyperlink } from '../../../../tool';
import CommentSection from '../../commentSection';
import useAnswerStyles from './useAnswerStyles';
import { Comment, User } from '../../../../types';
import useAnswerPageStyles from '../useAnswerPageStyles';
import useLayoutStyles from '../../../layout/useLayoutStyles';
import AwardComponent from '../../baseComponents/award';
import usePageLink from '../../../../hooks/useQuestion';
import { useAppSelector } from '../../../../redux/hooks';
import useGlobalStyles from '../../../useGlobalStyles';
import { currentUserSelector, ulistSelector } from '../../../../redux/slices/userSlice';

/**
 * Interface representing the props for the AnswerView component.
 *
 * - text The content of the answer.
 * - ansBy The username of the user who wrote the answer.
 * - meta Additional metadata related to the answer.
 * - comments An array of comments associated with the answer.
 * - handleAddComment Callback function to handle adding a new comment.
 */
interface AnswerProps {
  text: string;
  ansByUser: User | null;
  meta: string;
  comments: Comment[];
  handleAddComment: (comment: Comment) => void;
}

/**
 * AnswerView component that displays the content of an answer with the author's name and metadata.
 * The answer text is processed to handle hyperlinks, and a comment section is included.
 *
 * @param text The content of the answer.
 * @param ansBy The username of the answer's author.
 * @param meta Additional metadata related to the answer.
 * @param comments An array of comments associated with the answer.
 * @param handleAddComment Function to handle adding a new comment.
 */
const AnswerView = ({ text, ansByUser, meta, comments, handleAddComment }: AnswerProps) => {
  const { classes } = useAnswerStyles();
  const { classes: answerPageClasses } = useAnswerPageStyles();
  const { classes: layoutClasses } = useLayoutStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const users = useAppSelector(ulistSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const answerAuthor = ansByUser && users?.find(user => user.username === ansByUser.username);
  const { handleUser } = usePageLink();

  return (
    <div className={`${classes.answer} ${layoutClasses.rightPadding}`}>
      <div id='answerText' className={classes.answerText}>
        {handleHyperlink(text)}
      </div>
      <div
        className={classes.answerRight}
        onClick={() => {
          if (answerAuthor) handleUser(answerAuthor.username);
        }}>
        <Avatar
          className={globalClasses.userAvatar}
          src={`${process.env.PUBLIC_URL}/assets/profiles/${answerAuthor?.profilePicture}`}
        />
        <div className={classes.answerAuthorInfo}>
          <div
            className={`${classes.answerAuthor} ${!answerAuthor && classes.deletedAnswerAuthor}`}>
            {answerAuthor?.username || 'Deleted User'}
          </div>
          <div className={answerPageClasses.answerQuestionMeta}>{meta}</div>
        </div>
      </div>
      <div className={classes.awardButton}>
        <AwardComponent
          awardReceiver={ansByUser}
          disabled={answerAuthor === null || answerAuthor?._id === currentUser?._id}
        />
      </div>
      <CommentSection comments={comments} handleAddComment={handleAddComment} width='60%' />
    </div>
  );
};

export default AnswerView;
